import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import type { ApiClientError } from "./_errors";
import { apiJson } from "./_http";
import type {
	CreateDuelRequest,
	CreateDuelResponse,
	DuelDetailResponse,
	DuelMutationResponse,
	EarningsLeaderboardResponse,
	FamTrophiesResponse,
	LeaderboardBankrollTier,
	QuarterlyLeaderboardResponse,
	RecentDuelsResponse,
	WeeklyLeaderboardResponse,
} from "../types";

// TanStack Query hooks for the Fantasy Fams umbrella. Routes:
//   - GET    /api/fantasy-fams/{weekly,quarterly,earnings}/leaderboard   (public)
//   - GET    /api/duels/:id                                              (public)
//   - GET    /api/duels/recent                                           (public)
//   - POST   /api/duels/mutate                                           (authed, PM-only)
//   - POST   /api/duels/mutate/:id/accept                                (authed, PM-only)
//   - POST   /api/duels/mutate/:id/decline                               (authed, PM-only)
//   - GET    /api/fams/:slug/trophies                                    (authed)
//
// The /api/fams/:slug/fantasy/optin{,s} routes were removed in migration 041 —
// see fantasy_fams + fantasy_fam_members tables. New /api/fantasy-fams/* CRUD
// lands in the next PR.
//
// The /mutate/ split is intentional — see notes in functions/_middleware.ts.
// The Pages-Function middleware gates by URL path, so write routes live under
// a prefix the public matcher doesn't catch.

// --- Query keys -------------------------------------------------------------

export const fantasyFamsKeys = {
	all: ["fantasy-fams"] as const,
	weekly: (bankroll: LeaderboardBankrollTier, week?: string) =>
		[...fantasyFamsKeys.all, "weekly", bankroll, week ?? "current"] as const,
	quarterly: (bankroll: LeaderboardBankrollTier, quarter?: string) =>
		[...fantasyFamsKeys.all, "quarterly", bankroll, quarter ?? "current"] as const,
	earnings: (bankroll: LeaderboardBankrollTier, week?: string) =>
		[...fantasyFamsKeys.all, "earnings", bankroll, week ?? "current"] as const,
	trophies: (slug: string) => [...fantasyFamsKeys.all, "trophies", slug] as const,
	duel: (id: string) => [...fantasyFamsKeys.all, "duel", id] as const,
	recentDuels: (limit: number) =>
		[...fantasyFamsKeys.all, "duels", "recent", limit] as const,
};

// --- Leaderboard hooks ------------------------------------------------------

// Build the leaderboard query string. `bankroll` selects the competitive
// pool (always sent); `period` optionally scopes to a historical window.
function leaderboardQs(
	bankroll: LeaderboardBankrollTier,
	periodParam: "week" | "quarter",
	period?: string,
): string {
	const qs = new URLSearchParams({ bankroll });
	if (period) qs.set(periodParam, period);
	return `?${qs.toString()}`;
}

// useWeeklyLeaderboard — Fantasy Fams Weekly leaderboard for one bankroll
// pool. Pass `week` as the ISO-week tag (e.g. "2026-W22") to scope to a
// historical week; omit for the most-recent settled week. Refreshes every
// 60s so settle-day shuffles show without reload.
export function useWeeklyLeaderboard(
	bankroll: LeaderboardBankrollTier,
	week?: string,
): UseQueryResult<WeeklyLeaderboardResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.weekly(bankroll, week),
		queryFn: () =>
			apiJson<WeeklyLeaderboardResponse>(
				`/api/fantasy-fams/weekly/leaderboard${leaderboardQs(bankroll, "week", week)}`,
			),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
}

// useQuarterlyLeaderboard — Fantasy Fams Quarterly leaderboard for one
// bankroll pool.
export function useQuarterlyLeaderboard(
	bankroll: LeaderboardBankrollTier,
	quarter?: string,
): UseQueryResult<QuarterlyLeaderboardResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.quarterly(bankroll, quarter),
		queryFn: () =>
			apiJson<QuarterlyLeaderboardResponse>(
				`/api/fantasy-fams/quarterly/leaderboard${leaderboardQs(bankroll, "quarter", quarter)}`,
			),
		// Quarterly settles infrequently; cache for 5 min.
		staleTime: 5 * 60_000,
	});
}

// useEarningsLeaderboard — Fantasy Fams Earnings Showdown leaderboard for one
// bankroll pool.
export function useEarningsLeaderboard(
	bankroll: LeaderboardBankrollTier,
	week?: string,
): UseQueryResult<EarningsLeaderboardResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.earnings(bankroll, week),
		queryFn: () =>
			apiJson<EarningsLeaderboardResponse>(
				`/api/fantasy-fams/earnings/leaderboard${leaderboardQs(bankroll, "week", week)}`,
			),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
}

// Opt-in hooks removed in migration 041 — Fantasy Fam is its own entity now.
// The new useFantasyFams / useCreateFantasyFam hooks land in a follow-up PR.

// --- Duel hooks -------------------------------------------------------------

// useDuel — single duel spectator query. Polls every 30s while the duel is
// active so the head-to-head score updates without manual reload.
export function useDuel(id: string | undefined): UseQueryResult<DuelDetailResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.duel(id ?? "missing"),
		queryFn: () => {
			if (!id) throw new Error("missing duel id");
			return apiJson<DuelDetailResponse>(`/api/duels/${encodeURIComponent(id)}`);
		},
		enabled: !!id,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) return 30_000;
			// Stop polling once the duel terminates (winner declared, declined,
			// or expired). `forming` + `active` keep polling for live-tally
			// updates.
			const terminal = ["completed", "declined", "expired"];
			return terminal.includes(data.duel.status) ? false : 30_000;
		},
		staleTime: 0,
	});
}

// useRecentDuels — recent duel feed for the /fantasy/duels list.
export function useRecentDuels(limit = 20): UseQueryResult<RecentDuelsResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.recentDuels(limit),
		queryFn: () =>
			apiJson<RecentDuelsResponse>(
				`/api/duels/recent?limit=${encodeURIComponent(String(limit))}`,
			),
		staleTime: 60_000,
	});
}

// useCreateDuelMutation — PM challenges another Fam. Routes through the
// /api/duels/mutate authed prefix so the public /api/duels/* GETs don't get
// dragged into the auth check.
export function useCreateDuelMutation(): UseMutationResult<
	CreateDuelResponse,
	ApiClientError,
	CreateDuelRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body) =>
			apiJson<CreateDuelResponse>("/api/duels/mutate", {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamsKeys.all });
		},
	});
}

// useAcceptDuelMutation — opponent PM accepts an incoming challenge.
export function useAcceptDuelMutation(): UseMutationResult<
	DuelMutationResponse,
	ApiClientError,
	{ duelId: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ duelId }) =>
			apiJson<DuelMutationResponse>(
				`/api/duels/mutate/${encodeURIComponent(duelId)}/accept`,
				{ method: "POST" },
			),
		onSuccess: (_data, { duelId }) => {
			void qc.invalidateQueries({ queryKey: fantasyFamsKeys.duel(duelId) });
			void qc.invalidateQueries({ queryKey: fantasyFamsKeys.all });
		},
	});
}

// useDeclineDuelMutation — opponent PM declines an incoming challenge.
export function useDeclineDuelMutation(): UseMutationResult<
	DuelMutationResponse,
	ApiClientError,
	{ duelId: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ duelId }) =>
			apiJson<DuelMutationResponse>(
				`/api/duels/mutate/${encodeURIComponent(duelId)}/decline`,
				{ method: "POST" },
			),
		onSuccess: (_data, { duelId }) => {
			void qc.invalidateQueries({ queryKey: fantasyFamsKeys.duel(duelId) });
			void qc.invalidateQueries({ queryKey: fantasyFamsKeys.all });
		},
	});
}

// --- Trophy hook ------------------------------------------------------------

// useFamTrophies — list trophies awarded to a Fam. Cached per slug.
export function useFamTrophies(
	slug: string | undefined,
): UseQueryResult<FamTrophiesResponse> {
	return useQuery({
		queryKey: fantasyFamsKeys.trophies(slug ?? "missing"),
		queryFn: () => {
			if (!slug) throw new Error("missing fam slug");
			return apiJson<FamTrophiesResponse>(
				`/api/fams/${encodeURIComponent(slug)}/trophies`,
			);
		},
		enabled: !!slug,
		staleTime: 5 * 60_000,
	});
}

// --- Fantasy Fam entity hooks (migration 041) -------------------------------
//
// CRUD against the new ephemeral Fantasy Fam entity. Each user can be a
// captain or member of multiple Fantasy Fams (subject to the free-tier
// 1-active-at-a-time cap enforced by the DB trigger).

import type {
	AddFantasyFamMemberInput,
	CreateFantasyFamInput,
	CreateFantasyFamResponse,
	FantasyFam,
	FantasyFamDetailResponse,
	FantasyFamMember,
	ListMyFantasyFamsResponse,
	SubmitFantasyFamPicksInput,
} from "../types";

export const fantasyFamKeys = {
	all: ["fantasy-fam"] as const,
	mine: () => [...fantasyFamKeys.all, "mine"] as const,
	detail: (slug: string) => [...fantasyFamKeys.all, "detail", slug] as const,
};

export function useMyFantasyFams(): UseQueryResult<ListMyFantasyFamsResponse> {
	return useQuery({
		queryKey: fantasyFamKeys.mine(),
		queryFn: () => apiJson<ListMyFantasyFamsResponse>("/api/fantasy-fams/me"),
		staleTime: 60_000,
	});
}

export function useFantasyFam(
	slug: string | undefined,
): UseQueryResult<FantasyFamDetailResponse> {
	return useQuery({
		queryKey: fantasyFamKeys.detail(slug ?? "missing"),
		queryFn: () => {
			if (!slug) throw new Error("missing slug");
			return apiJson<FantasyFamDetailResponse>(
				`/api/fantasy-fams/${encodeURIComponent(slug)}`,
			);
		},
		enabled: !!slug,
		staleTime: 30_000,
	});
}

export function useCreateFantasyFam(): UseMutationResult<
	CreateFantasyFamResponse,
	ApiClientError,
	CreateFantasyFamInput
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input) =>
			apiJson<CreateFantasyFamResponse>("/api/fantasy-fams", {
				method: "POST",
				body: JSON.stringify(input),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.mine() });
		},
	});
}

export function useAddFantasyFamMember(
	slug: string,
): UseMutationResult<
	{ member: FantasyFamMember },
	ApiClientError,
	AddFantasyFamMemberInput
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input) =>
			apiJson<{ member: FantasyFamMember }>(
				`/api/fantasy-fams/${encodeURIComponent(slug)}/members`,
				{ method: "POST", body: JSON.stringify(input) },
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.detail(slug) });
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.mine() });
		},
	});
}

export function useRemoveFantasyFamMember(
	slug: string,
): UseMutationResult<void, ApiClientError, { userId: string }> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ userId }) =>
			apiJson<void>(
				`/api/fantasy-fams/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
				{ method: "DELETE" },
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.detail(slug) });
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.mine() });
		},
	});
}

export function useSubmitFantasyFamPicks(
	slug: string,
): UseMutationResult<
	{ fantasyFam: FantasyFam },
	ApiClientError,
	SubmitFantasyFamPicksInput
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input) =>
			apiJson<{ fantasyFam: FantasyFam }>(
				`/api/fantasy-fams/${encodeURIComponent(slug)}/picks`,
				{ method: "POST", body: JSON.stringify(input) },
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.detail(slug) });
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.mine() });
		},
	});
}

interface MintInviteLinkResult {
	token: string;
	url: string;
	expiresAt: number;
}

// useMintFantasyFamInviteLink — PM mints a sharable invite link. Stateless
// HMAC token; nothing to invalidate (the link doesn't mutate the entity).
export function useMintFantasyFamInviteLink(
	slug: string,
): UseMutationResult<MintInviteLinkResult, ApiClientError, void> {
	return useMutation({
		mutationFn: () =>
			apiJson<MintInviteLinkResult>(
				`/api/fantasy-fams/${encodeURIComponent(slug)}/invite-link`,
				{ method: "POST", body: JSON.stringify({}) },
			),
	});
}

// useRedeemFantasyFamInvite — the joining user POSTs the token; the Worker
// adds them as a member + returns the Fantasy Fam so the caller can
// navigate to it.
export function useRedeemFantasyFamInvite(): UseMutationResult<
	{ fantasyFam: FantasyFam; alreadyMember: boolean },
	ApiClientError,
	{ token: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ token }) =>
			apiJson<{ fantasyFam: FantasyFam; alreadyMember: boolean }>(
				"/api/fantasy-fams/redeem-invite",
				{ method: "POST", body: JSON.stringify({ token }) },
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: fantasyFamKeys.mine() });
		},
	});
}
